import type {
  FieldedStatus,
  MissionAssessmentInterAiGenerationDataDto,
  VerificationConfidence,
} from '@@types/api.types';
import { BadgeList } from '@components/shared/badge-list.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { useGetDigitalIntegrations } from '@queries/digital-integration.query';
import { useLlmModelsQueryOptions } from '@queries/llm.query';
import { useGetOperationalIntegrations } from '@queries/operational-integration.query';
import { useGetPhysicalIntegrations } from '@queries/physical-integration.query';
import { useQuery } from '@tanstack/react-query';
import { FIELDED_STATUS_LABELS, VERIFICATION_CONFIDENCE_LABELS } from '@utils/constants';
import { CheckIcon, XIcon } from 'lucide-react';
import React, { useMemo } from 'react';

type CategoryOption = {
  id: string;
  name: string;
  subcategories: Array<{
    id: string;
    name: string;
  }>;
};

type MissionAssessmentInteroperabilitySuggestionProps = {
  categoryOptions: CategoryOption[];
  className?: string;
  data: MissionAssessmentInterAiGenerationDataDto | null;
  isGenerating: boolean;
  /**
   * The AI model used to generate the suggestion
   */
  model?: string;
  onAccept: () => void;
  onReject: () => void;
};

const formatEnumValue = (value: string): string => {
  if (value in FIELDED_STATUS_LABELS) {
    return FIELDED_STATUS_LABELS[value as FieldedStatus];
  }
  if (value in VERIFICATION_CONFIDENCE_LABELS) {
    return VERIFICATION_CONFIDENCE_LABELS[value as VerificationConfidence];
  }
  return value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const SuggestionField: React.FC<{
  label: string;
  value: string | number | undefined;
  className?: string;
  expandable?: boolean;
}> = ({ label, value, className, expandable = false }) => {
  if (value === undefined || value === null || value === '') return null;

  const displayValue = typeof value === 'string' && value.includes('_') ? formatEnumValue(value) : value;

  return (
    <div className={cn('p-3 border border-gray-200 rounded-lg', className)}>
      <div className="text-xs font-medium text-gray-500 mb-2">{label}</div>
      {expandable && typeof displayValue === 'string' ? (
        <ExpandableText
          text={displayValue}
          maxLines={3}
          className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap"
        />
      ) : (
        <div className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">{displayValue}</div>
      )}
    </div>
  );
};

/**
 * Component that displays the AI model name using the proper label from the API
 */
const ModelDisplay: React.FC<{ model?: string }> = ({ model }) => {
  const llmModelsQueryOptions = useLlmModelsQueryOptions();
  const { data: llmModels } = useQuery(llmModelsQueryOptions);

  const modelLabel = React.useMemo(() => {
    // Handle cases where no model is selected
    if (!model || model === 'auto') {
      return 'Auto';
    }

    if (!llmModels) return model;
    const foundModel = llmModels.find((m) => m.value === model);
    return foundModel?.label || model;
  }, [llmModels, model]);

  return <span>Model used: {modelLabel}</span>;
};

type MissionAssessmentInterSuggestionFieldsProps = {
  categoryOptions: CategoryOption[];
  data: MissionAssessmentInterAiGenerationDataDto;
};

/**
 * Tone-neutral field grid shared by the inline suggestion card and the
 * fullscreen chat artifact panel.
 */
export const MissionAssessmentInterSuggestionFields: React.FC<MissionAssessmentInterSuggestionFieldsProps> = (
  props,
) => {
  const { categoryOptions, data } = props;

  const { data: digitalIntegrations } = useGetDigitalIntegrations();
  const { data: operationalIntegrations } = useGetOperationalIntegrations();
  const { data: physicalIntegrations } = useGetPhysicalIntegrations();

  const digitalIntegrationItems = useMemo(() => {
    if (!data?.digitalIntegrationIds || data.digitalIntegrationIds.length === 0 || !digitalIntegrations)
      return undefined;
    return data.digitalIntegrationIds
      .map((id) => {
        const integration = digitalIntegrations.find((i) => i.id === id);
        return integration ? { id: integration.id, name: integration.name } : null;
      })
      .filter((item): item is { id: string; name: string } => item !== null);
  }, [data?.digitalIntegrationIds, digitalIntegrations]);

  const operationalIntegrationItems = useMemo(() => {
    if (!data?.operationalIntegrationIds || data.operationalIntegrationIds.length === 0 || !operationalIntegrations)
      return undefined;
    return data.operationalIntegrationIds
      .map((id) => {
        const integration = operationalIntegrations.find((i) => i.id === id);
        return integration ? { id: integration.id, name: integration.name } : null;
      })
      .filter((item): item is { id: string; name: string } => item !== null);
  }, [data?.operationalIntegrationIds, operationalIntegrations]);

  const physicalIntegrationItems = useMemo(() => {
    if (!data?.physicalIntegrationIds || data.physicalIntegrationIds.length === 0 || !physicalIntegrations)
      return undefined;
    return data.physicalIntegrationIds
      .map((id) => {
        const integration = physicalIntegrations.find((i) => i.id === id);
        return integration ? { id: integration.id, name: integration.name } : null;
      })
      .filter((item): item is { id: string; name: string } => item !== null);
  }, [data?.physicalIntegrationIds, physicalIntegrations]);

  const getCategoryName = (categoryId: string): string | undefined => {
    const category = categoryOptions.find((cat) => cat.id === categoryId);
    return category?.name;
  };

  const getSubcategoryName = (categoryId: string, subcategoryId: string): string | undefined => {
    const category = categoryOptions.find((cat) => cat.id === categoryId);
    const subcategory = category?.subcategories.find((sub) => sub.id === subcategoryId);
    return subcategory?.name;
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <SuggestionField
          label="Name"
          value={data.name}
        />
        <SuggestionField
          label="Reference"
          value={data.reference}
        />
      </div>

      <SuggestionField
        label="Description"
        value={data.description}
        expandable
      />

      <div className="grid gap-4 md:grid-cols-2">
        <SuggestionField
          label="Category"
          value={getCategoryName(data.categoryId)}
        />
        <SuggestionField
          label="Subcategory"
          value={getSubcategoryName(data.categoryId, data.subcategoryId)}
        />
      </div>

      <SuggestionField
        label="Notes"
        value={data.notes}
        expandable
      />

      <div className="grid gap-4 md:grid-cols-3">
        <SuggestionField
          label="TRL"
          value={data.trl}
        />
        <SuggestionField
          label="MRL"
          value={data.mrl}
        />
        <SuggestionField
          label="Fielded Status"
          value={data.fieldedStatus && FIELDED_STATUS_LABELS[data.fieldedStatus]}
        />
      </div>

      {(data.priorityScore !== undefined || data.priorityRationale) && (
        <div className="p-4 border border-gray-200 rounded-lg space-y-3">
          <div className="text-sm font-semibold text-gray-700 tracking-wide">Priority</div>

          <SuggestionField
            label="Priority Score"
            value={data.priorityScore}
          />

          <SuggestionField
            label="Priority Rationale"
            value={data.priorityRationale}
            expandable
          />
        </div>
      )}

      {(data.verificationConfidence || data.verificationNotes) && (
        <div className="p-4 border border-gray-200 rounded-lg space-y-3">
          <div className="text-sm font-semibold text-gray-700 tracking-wide">Verification</div>

          <SuggestionField
            label="Verification Confidence"
            value={data.verificationConfidence && VERIFICATION_CONFIDENCE_LABELS[data.verificationConfidence]}
          />

          <SuggestionField
            label="Verification Notes"
            value={data.verificationNotes}
            expandable
          />
        </div>
      )}

      {((digitalIntegrationItems && digitalIntegrationItems.length > 0) ||
        (operationalIntegrationItems && operationalIntegrationItems.length > 0) ||
        (physicalIntegrationItems && physicalIntegrationItems.length > 0)) && (
        <div className="p-4 border border-gray-200 rounded-lg space-y-4">
          <div className="text-sm font-semibold text-gray-700 tracking-wide">Integrations</div>

          <div className="space-y-3">
            {digitalIntegrationItems && digitalIntegrationItems.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 tracking-wide mb-2">Digital</div>
                <BadgeList
                  items={digitalIntegrationItems}
                  badgeVariant="outline"
                  size="sm"
                  limit={5}
                />
              </div>
            )}

            {operationalIntegrationItems && operationalIntegrationItems.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 tracking-wide mb-2">Operational</div>
                <BadgeList
                  items={operationalIntegrationItems}
                  badgeVariant="outline"
                  size="sm"
                  limit={5}
                />
              </div>
            )}

            {physicalIntegrationItems && physicalIntegrationItems.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 tracking-wide mb-2">Physical</div>
                <BadgeList
                  items={physicalIntegrationItems}
                  badgeVariant="outline"
                  size="sm"
                  limit={5}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const MissionAssessmentInteroperabilitySuggestion: React.FC<MissionAssessmentInteroperabilitySuggestionProps> = (
  props,
) => {
  const { categoryOptions, className, data, model, onAccept, onReject } = props;

  if (!data) {
    return null;
  }

  return (
    <div className={cn('p-4 bg-blue-50/50 border border-blue-300 rounded-lg shadow-sm', className)}>
      <MissionAssessmentInterSuggestionFields
        categoryOptions={categoryOptions}
        data={data}
      />

      <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t">
        <div className="text-xs text-gray-500 font-medium">
          <ModelDisplay model={model} />
        </div>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="destructive"
            onClick={onReject}
          >
            <XIcon className="size-4" />
            Reject
          </Button>

          <Button
            size="sm"
            variant="primary"
            onClick={onAccept}
          >
            <CheckIcon className="size-4" />
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};
